import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { useAddNote, useUserCard } from '@/lib/queries';
import { formatDate } from '@/lib/utils';

export function UserDetailPage() {
  const { discordId = '' } = useParams();
  const card = useUserCard(discordId);
  const addNote = useAddNote();
  const [note, setNote] = useState('');

  async function handleAddNote(e: React.FormEvent) {
    e.preventDefault();
    if (!note.trim()) return;
    try {
      await addNote.mutateAsync({ discordId, note });
      toast.success('Note added');
      setNote('');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  if (card.isLoading) return <p className="text-muted-foreground">loading…</p>;
  if (!card.data) return <p className="text-muted-foreground">not found</p>;

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/users">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to list
        </Link>
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="font-mono">{discordId}</CardTitle>
          <CardDescription>
            {card.data.user
              ? `${card.data.user.username ?? '—'} · joined ${
                  card.data.user.joinedServerAt
                    ? formatDate(card.data.user.joinedServerAt)
                    : 'unknown'
                }`
              : 'Customer is not in the database (no users row yet)'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-2">
            <Stat label="Tickets" value={card.data.ticketCount} />
            <Stat label="Notes" value={card.data.notes.length} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Admin notes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {card.data.notes.length > 0 ? (
            <div className="space-y-3">
              {card.data.notes.map((n) => (
                <div key={n.id} className="rounded-md border border-border p-3">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="font-mono">{n.authorDiscordId}</span>
                    <span>{formatDate(n.createdAt)}</span>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm">{n.note}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No notes yet</p>
          )}

          <form onSubmit={handleAddNote} className="space-y-2">
            <Label htmlFor="note">New note</Label>
            <textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={2000}
              rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="Internal note (admins only)"
            />
            <Button type="submit" size="sm" disabled={!note.trim() || addNote.isPending}>
              Add note
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
    </div>
  );
}
